/**
 * WeaveIn.ai Platform - Members Hook
 * React hook for member management with custom fields
 */

import { useState, useEffect, useCallback } from 'react';
import { membersAPI, Member, MemberCreate, MemberListResponse } from '@/services/api/members';
import { getApiErrorMessage } from '@/utils/apiErrors';

interface UseMembersOptions {
  page?: number;
  page_size?: number;
  search?: string;
  autoFetch?: boolean;
  program_id?: number;
  enrollment_status?: string;
  group_id?: number;
  coordinator_id?: number;
  gender?: string;
  language?: string;
  insurance_type?: string;
  custom_field?: string;
  filter_chip?: string;
  /** 'active' (default) | 'archived' | 'all' — see membersAPI.listMembers. */
  membership_status?: 'active' | 'archived' | 'all';
  workforce_only?: boolean;
  sort_by?: string;
  sort_order?: string;
}

interface UseMembersReturn {
  members: Member[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  /**
   * Resolves with the new member, or THROWS with a displayable message.
   *
   * It used to swallow the failure and resolve with `null`, which made every
   * save error invisible: callers do `const created = await createMember(...);
   * if (created) navigate(...)`, so a failure just... did nothing. The button
   * stopped spinning and no message appeared anywhere. Somos promotoras hit a
   * guaranteed 422 on every create and reported "nothing happens when I click
   * Create Member" — the API had been answering with the reason all along.
   * Throwing is what makes the caller's existing catch block run.
   */
  createMember: (data: MemberCreate) => Promise<Member>;
  /** Resolves with the updated member, or THROWS — see createMember. */
  updateMember: (id: number, data: Partial<MemberCreate>) => Promise<Member>;
  deleteMember: (id: number) => Promise<boolean>;
  /** Undo an archive (soft delete). Same permission as deleting. */
  restoreMember: (id: number) => Promise<boolean>;
  getMember: (id: number) => Promise<Member | null>;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
}

export default function useMembers(options: UseMembersOptions = {}): UseMembersReturn {
  const {
    page: initialPage = 1,
    page_size = 50,
    search: initialSearch = '',
    autoFetch = true,
    program_id, enrollment_status, group_id, coordinator_id,
    gender, language, insurance_type, custom_field,
    filter_chip, membership_status, workforce_only,
    sort_by, sort_order,
  } = options;

  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [search, setSearch] = useState(initialSearch);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: MemberListResponse = await membersAPI.listMembers({
        page,
        page_size,
        search: search || undefined,
        program_id, enrollment_status, group_id, coordinator_id,
        gender, language, insurance_type, custom_field,
        filter_chip, membership_status, workforce_only,
        sort_by, sort_order,
      });

      setMembers(response.members);
      setTotal(response.total);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch members';
      setError(errorMessage);
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  }, [page, page_size, search, program_id, enrollment_status, group_id, coordinator_id, gender, language, insurance_type, custom_field, filter_chip, membership_status, workforce_only, sort_by, sort_order]);

  useEffect(() => {
    if (autoFetch) {
      fetchMembers();
    }
  }, [autoFetch, fetchMembers]);

  const createMember = async (data: MemberCreate): Promise<Member> => {
    setLoading(true);
    setError(null);

    try {
      const newMember = await membersAPI.createMember(data);

      // Optimistic update - add to list
      setMembers((prev) => [newMember, ...prev]);
      setTotal((prev) => prev + 1);

      return newMember;
    } catch (err: any) {
      // getApiErrorMessage flattens FastAPI's 422 `detail`, which is an ARRAY of
      // {loc, msg} objects. The old code read `err.response.data.detail`
      // directly, so on the most common save failure it handed the caller an
      // array — React cannot render that as a child even if a caller had tried.
      const errorMessage = getApiErrorMessage(err) || 'Failed to create member';
      setError(errorMessage);
      console.error('Error creating member:', err);
      // Rethrow: returning null here is what made save failures silent.
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateMember = async (
    id: number,
    data: Partial<MemberCreate>
  ): Promise<Member> => {
    setLoading(true);
    setError(null);

    try {
      const updatedMember = await membersAPI.updateMember(id, data);

      // Update in local state
      setMembers((prev) =>
        prev.map((member) => (member.id === id ? updatedMember : member))
      );

      return updatedMember;
    } catch (err: any) {
      const errorMessage = getApiErrorMessage(err) || 'Failed to update member';
      setError(errorMessage);
      console.error('Error updating member:', err);
      // Rethrow — see createMember. Edit Member was equally silent.
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteMember = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await membersAPI.deleteMember(id);

      // Remove from local state
      setMembers((prev) => prev.filter((member) => member.id !== id));
      setTotal((prev) => prev - 1);

      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete member';
      setError(errorMessage);
      console.error('Error deleting member:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const restoreMember = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await membersAPI.restoreMember(id);
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to restore member';
      setError(errorMessage);
      console.error('Error restoring member:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getMember = async (id: number): Promise<Member | null> => {
    setLoading(true);
    setError(null);

    try {
      const member = await membersAPI.getMember(id);
      return member;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch member';
      setError(errorMessage);
      console.error('Error fetching member:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    members,
    total,
    page,
    pageSize: page_size,
    loading,
    error,
    createMember,
    updateMember,
    deleteMember,
    restoreMember,
    getMember,
    refetch: fetchMembers,
    setPage,
    setSearch,
  };
}
